/**
 * Словарь видов камня.
 *
 * Тег, которым уже помечены камни, не удаляется: убрать его — значит соврать
 * в их карточках. Вместо удаления он прячется из выбора у стенда, а в снятом
 * остаётся как было.
 *
 * Порядок здесь важнее алфавита: то, что встречается каждый день, должно
 * лежать первым — у стенда листать некогда.
 */
import { useEffect, useState } from 'react';
import { db, enqueue, seedTags, tagId, type LocalTag } from '../db';
import { syncNow } from '../sync';
import { ulid, plural } from '@core';

export default function Tags({ onBack }: { onBack: () => void }) {
  const [tags, setTags] = useState<LocalTag[]>([]);
  const [used, setUsed] = useState<Record<string, number>>({});
  const [adding, setAdding] = useState('');
  const [problem, setProblem] = useState('');

  async function load() {
    await seedTags();
    const all = await db.tags.orderBy('sort').toArray();
    setTags(all);

    // Сколько камней помечено каждым — чтобы было видно, чем пользуются.
    const facts = await db.facts.filter(
      (f) => f.attribute === 'stone_type' && f.sourceType === 'gemologist').toArray();
    const count: Record<string, number> = {};
    for (const f of facts) {
      for (const label of String(f.valueText ?? '').split(' · ')) {
        const id = tagId(label.trim());
        if (label.trim()) count[id] = (count[id] ?? 0) + 1;
      }
    }
    setUsed(count);
  }

  useEffect(() => { void load(); }, []);

  async function save(row: LocalTag) {
    await db.tags.put(row);
    await enqueue('tag.upsert', row, ulid());
    void syncNow();
    await load();
  }

  async function add() {
    const label = adding.trim();
    if (!label) return;
    const id = tagId(label);
    if (tags.some((t) => t.id === id)) {
      setProblem(`«${label}» уже есть в списке.`);
      return;
    }
    setProblem('');
    setAdding('');
    await save({ id, label, sort: 5, hidden: false, createdAt: new Date().toISOString() });
  }

  /** Поднять тег наверх: у стенда листать некогда, частое должно быть первым. */
  async function raise(t: LocalTag) {
    const first = tags[0];
    if (!first || first.id === t.id) return;
    await save({ ...t, sort: first.sort - 1 });
  }

  const visible = tags.filter((t) => !t.hidden);
  const hidden = tags.filter((t) => t.hidden);

  return (
    <div className="app tabbed">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Ещё</button>
        <h1 className="t">Виды камня</h1>
        <p className="meta">
          {visible.length} {plural(visible.length, 'тег', 'тега', 'тегов')} · выбираются на кадре
        </p>
      </div>

      <div className="scroll">
        <div className="field" style={{ marginTop: 4 }}>
          <span>Добавить вид</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={adding} onChange={(e) => setAdding(e.target.value)}
                   placeholder="Например, Кунцит"
                   onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
            <button className="btn btn-primary" style={{ flex: 'none', width: 'auto', padding: '0 18px' }}
                    disabled={!adding.trim()} onClick={() => void add()}>
              Добавить
            </button>
          </div>
          {problem
            ? <span className="hint" style={{ color: 'var(--alert)' }}>{problem}</span>
            : <span className="hint">Появится на всех ваших устройствах.</span>}
        </div>

        <div className="head"><h3>В выборе у стенда</h3><span>{visible.length}</span></div>
        <div className="list">
          {visible.map((t) => (
            <div className="row" key={t.id}>
              <span className="grow">
                <span className="nm">{t.label}</span>
                <span className="sub">
                  {used[t.id] ? `${used[t.id]} ${plural(used[t.id], 'камень', 'камня', 'камней')}` : 'ещё не использован'}
                </span>
              </span>
              <button className="btn-quiet" onClick={() => void raise(t)} aria-label="Поднять наверх">↑</button>
              <button className="btn-quiet" onClick={() => void save({ ...t, hidden: true })}>Скрыть</button>
            </div>
          ))}
        </div>

        {hidden.length > 0 && (
          <>
            <div className="head"><h3>Скрытые</h3><span>{hidden.length}</span></div>
            <div className="list">
              {hidden.map((t) => (
                <div className="row" key={t.id}>
                  <span className="grow">
                    <span className="nm" style={{ color: 'var(--ink-3)' }}>{t.label}</span>
                    <span className="sub">
                      {used[t.id] ? `остаётся у ${used[t.id]} ${plural(used[t.id], 'камня', 'камней', 'камней')}` : 'не используется'}
                    </span>
                  </span>
                  <button className="btn-quiet" onClick={() => void save({ ...t, hidden: false })}>Вернуть</button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="memo">
          Удаления здесь нет намеренно. Камни, уже помеченные тегом, останутся
          помеченными — иначе в их карточках появилась бы неправда. Скрытый тег
          просто не предлагается при съёмке.
        </div>
      </div>
    </div>
  );
}
